import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { Typography, CircularProgress } from '@material-ui/core'
import InputAdornment from '@material-ui/core/InputAdornment'
import SearchIcon from '@material-ui/icons/Search'
import TextField from '@material-ui/core/TextField'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableSortLabel from '@mui/material/TableSortLabel'
import Tooltip from '@mui/material/Tooltip'
import Link from '@mui/material/Link'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import { history } from '_state/helpers'
import { overallActions } from '../../../_state/actions/overall.actions'
import { organizationActions } from '../../../_state/actions'

const useStyles = makeStyles((theme) => ({
    root: {
        marginLeft: '25px',
        marginRight: '25px',
        marginTop: '25px',
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
    },
    toolbarRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
        gap: '20px',
    },
    headerCell: {
        fontWeight: 600,
    },
    nameLink: {
        cursor: 'pointer',
    },
    warningIcon: {
        color: '#d32f2f',
        verticalAlign: 'middle',
        marginLeft: '6px',
    },
    summaryRow: {
        fontWeight: 600,
        backgroundColor: '#fafafa',
    },
    spinnerWrap: {
        display: 'flex',
        justifyContent: 'center',
        padding: '40px',
    },
}))

const PERIOD_OPTIONS = [
    { value: 'ThisWeek', label: 'This week' },
    { value: 'ThisMonth', label: 'This month' },
    { value: 'ThisYear', label: 'This year' },
    { value: 'AllTime', label: 'All time' },
]

const COLUMNS = [
    { id: 'schoolName', label: 'Organization', numeric: false },
    { id: 'usedLicenses', label: 'Users (license holders)', numeric: true },
    { id: 'numberOfLicenses', label: 'Licenses assigned', numeric: true },
    { id: 'licenseStatus', label: 'License usage', numeric: true },
    { id: 'lessonsPassed', label: 'Lessons passed', numeric: true },
    { id: 'minutesSpent', label: 'Time spent', numeric: true },
]

const formatHours = (minutes) => {
    if (!minutes || minutes <= 0) return '0h'
    const hours = Math.round(minutes / 6) / 10
    return `${hours}h`
}

const formatLicenseUsage = (row) => {
    const denom = row.numberOfLicenses > 0 ? row.numberOfLicenses : '∞'
    return `${row.usedLicenses} / ${denom}`
}

const compareValues = (a, b, orderBy) => {
    let aVal
    let bVal
    if (orderBy === 'licenseStatus') {
        aVal = a.numberOfLicenses > 0 ? a.usedLicenses / a.numberOfLicenses : -1
        bVal = b.numberOfLicenses > 0 ? b.usedLicenses / b.numberOfLicenses : -1
    } else {
        aVal = a[orderBy]
        bVal = b[orderBy]
    }

    if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal)
    }
    if (aVal == null && bVal == null) return 0
    if (aVal == null) return -1
    if (bVal == null) return 1
    return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
}

const OverallOrganizationStats = (props) => {
    const classes = useStyles()

    const [searchToken, setSearchToken] = useState('')
    const [orderBy, setOrderBy] = useState('schoolName')
    const [order, setOrder] = useState('asc')
    const [period, setPeriod] = useState('ThisYear')
    const [hideInactive, setHideInactive] = useState(true)

    useEffect(() => {
        props.dispatch(overallActions.getAllOrganizationsUsageStats(period))
    }, [period])

    const data = props.overall?.allOrganizationsUsageStats
    const loading = props.overall?.allOrganizationsUsageStatsLoading

    const filteredAndSorted = useMemo(() => {
        if (!data?.rows) return []
        const lower = searchToken.trim().toLowerCase()
        let filtered = lower
            ? data.rows.filter((r) => r.schoolName?.toLowerCase().includes(lower))
            : [...data.rows]
        if (hideInactive) {
            filtered = filtered.filter((r) => (r.lessonsPassed || 0) > 0 || (r.minutesSpent || 0) > 0)
        }
        const direction = order === 'asc' ? 1 : -1
        return filtered.sort((a, b) => direction * compareValues(a, b, orderBy))
    }, [data, searchToken, order, orderBy, hideInactive])

    const totals = useMemo(() => {
        return filteredAndSorted.reduce(
            (acc, r) => ({
                usedLicenses: acc.usedLicenses + (r.usedLicenses || 0),
                numberOfLicenses: acc.numberOfLicenses + (r.numberOfLicenses || 0),
                lessonsPassed: acc.lessonsPassed + (r.lessonsPassed || 0),
                minutesSpent: acc.minutesSpent + (r.minutesSpent || 0),
            }),
            {
                usedLicenses: 0,
                numberOfLicenses: 0,
                lessonsPassed: 0,
                minutesSpent: 0,
            }
        )
    }, [filteredAndSorted])

    const handleSort = (columnId) => {
        if (orderBy === columnId) {
            setOrder(order === 'asc' ? 'desc' : 'asc')
        } else {
            setOrderBy(columnId)
            setOrder(columnId === 'schoolName' ? 'asc' : 'desc')
        }
    }

    const handlePeriodChange = (event, newPeriod) => {
        if (newPeriod) {
            setPeriod(newPeriod)
        }
    }

    // Switches the selected organization and lands on its dashboard.
    //
    // This used to open /admin/edit-organization, which is an Admin only route - reporting users,
    // who are the point of this table, could switch organization and then be refused the page they
    // were sent to. The dashboard is reachable by every admin panel role.
    //
    // The navigation is done here rather than through setCurrentOrganization's
    // goToDashboardAfterChange flag on purpose: that flag is only honoured after the saga has
    // finished loading the organization's lessons, and any failure along the way leaves the user on
    // this page with the organization already switched underneath them.
    const handleOpenOrganization = (organizationId) => {
        props.dispatch(organizationActions.setCurrentOrganization(organizationId, false))
        history.push('/dashboard/')
    }

    const periodLabel = PERIOD_OPTIONS.find((o) => o.value === period)?.label

    return (
        <div className={classes.root}>
            <div className={classes.headerRow}>
                <Typography variant="h4">
                    Organization statistics{periodLabel ? ` - ${periodLabel}` : ''}
                </Typography>
                <TextField
                    placeholder="Search by name"
                    value={searchToken}
                    onChange={(e) => setSearchToken(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>

            <div className={classes.toolbarRow}>
                <ToggleButtonGroup
                    value={period}
                    exclusive
                    size="small"
                    onChange={handlePeriodChange}
                    aria-label="Time period">
                    {PERIOD_OPTIONS.map((option) => (
                        <ToggleButton key={option.value} value={option.value}>
                            {option.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <FormControlLabel
                    control={
                        <Switch
                            checked={hideInactive}
                            onChange={(e) => setHideInactive(e.target.checked)}
                        />
                    }
                    label="Hide inactive organizations"
                />
            </div>

            {loading && (
                <div className={classes.spinnerWrap}>
                    <CircularProgress />
                </div>
            )}

            {!loading && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {COLUMNS.map((col) => (
                                    <TableCell
                                        key={col.id}
                                        align={col.numeric ? 'right' : 'left'}
                                        className={classes.headerCell}
                                        sortDirection={orderBy === col.id ? order : false}>
                                        <TableSortLabel
                                            active={orderBy === col.id}
                                            direction={orderBy === col.id ? order : 'asc'}
                                            onClick={() => handleSort(col.id)}>
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredAndSorted.map((row) => (
                                <TableRow key={row.organizationId} hover>
                                    <TableCell>
                                        <Tooltip title="Switch to this organization">
                                            <Link
                                                component="button"
                                                underline="hover"
                                                className={classes.nameLink}
                                                onClick={() => handleOpenOrganization(row.organizationId)}>
                                                {row.schoolName}
                                            </Link>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">{row.usedLicenses}</TableCell>
                                    <TableCell align="right">
                                        {row.numberOfLicenses > 0 ? row.numberOfLicenses : '∞'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatLicenseUsage(row)}
                                        {row.exceedsLicenses && (
                                            <Tooltip title="Exceeds licenses">
                                                <ReportProblemOutlinedIcon
                                                    fontSize="small"
                                                    className={classes.warningIcon}
                                                />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{row.lessonsPassed}</TableCell>
                                    <TableCell align="right">{formatHours(row.minutesSpent)}</TableCell>
                                </TableRow>
                            ))}
                            {filteredAndSorted.length > 0 && (
                                <TableRow className={classes.summaryRow}>
                                    <TableCell>Totals ({filteredAndSorted.length})</TableCell>
                                    <TableCell align="right">{totals.usedLicenses}</TableCell>
                                    <TableCell align="right">{totals.numberOfLicenses}</TableCell>
                                    <TableCell align="right" />
                                    <TableCell align="right">{totals.lessonsPassed}</TableCell>
                                    <TableCell align="right">{formatHours(totals.minutesSpent)}</TableCell>
                                </TableRow>
                            )}
                            {filteredAndSorted.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={COLUMNS.length} align="center">
                                        No organizations match the current filter.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </div>
    )
}

const mapStateToProps = (state) => ({
    overall: state.overall,
    user: state.user,
})

export default connect(mapStateToProps)(OverallOrganizationStats)
